import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { ShieldCheck, FileCheck2, Info, Sparkles, Wallet, UserRound, Lock, X, Network, AlertTriangle } from "lucide-react";

/* -----------------------------------------------------------
   KATLAS visibility primitives.

   Modes:
     • live      — node reachable + attesting
     • fallback  — live requested but node didn't attest
     • simulation — node not configured / mode=simulation

   The hook below polls `/api/katlas/status` once on mount so every chip,
   drawer and record reflects whatever the backend reports.
   ----------------------------------------------------------- */

const TONES = {
  live:       { cls: "bg-[#E8F1EA] text-[#1F4A2D] border-[#A8C5B0]", label: "KATLAS node active" },
  fallback:   { cls: "bg-[#FFF4E2] text-[#7A4A00] border-[#E9CC9A]", label: "KATLAS simulation fallback" },
  simulation: { cls: "bg-[#FFF7F2] text-[#7A2E0C] border-[#F2C4A6]", label: "KATLAS governance simulation" },
  unknown:    { cls: "bg-[#F4F5F6] text-[#555]      border-[#D5D7DA]", label: "KATLAS status checking…" },
};

function chipMode(status) {
  if (!status) return "unknown";
  if (status.status === "active") return "live";
  // We treat node-misconfigured / unreachable as simulation fallback only when
  // the operator explicitly chose live mode and fallback is allowed.
  if (status.mode === "live" && (status.status === "unreachable" || status.status === "misconfigured")) return "fallback";
  return "simulation";
}

function attestationMode(att) {
  if (!att) return null;
  if (att.mode === "live") return "live";
  if (att.mode === "fallback") return "fallback";
  return "simulation";
}

/** One-shot fetch of /api/katlas/status, cached across mounts in module scope. */
let _statusPromise = null;
export function useKatlasStatus() {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!_statusPromise) _statusPromise = api.katlasStatus().catch(() => null);
    _statusPromise.then(setS);
  }, []);
  return s;
}

export function KatlasModeChip({ size = "sm", overrideMode = null }) {
  const status = useKatlasStatus();
  const mode = overrideMode || chipMode(status);
  const t = TONES[mode] || TONES.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1 ${size === "lg" ? "text-[11px] px-2 py-1" : "text-[10px] px-1.5 py-0.5"} tracking-eyebrow font-mono-ui rounded-full border ${t.cls}`}
      data-testid="katlas-mode-chip"
      data-mode={mode}
      title={status?.node_id ? `Node: ${status.node_id}` : "KATLAS attestation layer"}
    >
      {mode === "live" ? <Network className="h-3 w-3" /> : mode === "fallback" ? <AlertTriangle className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
      {t.label.toUpperCase()}
    </span>
  );
}

export function PolicyGatePassedChip({ passed = true }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui rounded-full border px-1.5 py-0.5 ${passed ? "bg-[#E8F1EA] text-[#2E5B3D] border-[#B7D1BD]" : "bg-[#FBE9E7] text-[#7A2E16] border-[#E9B4AA]"}`}
      data-testid="katlas-policy-gate-chip"
    >
      <ShieldCheck className="h-3 w-3" /> Policy gate: {passed ? "passed" : "blocked"}
    </span>
  );
}

export function ReceiptSignedChip() {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui rounded-full border px-1.5 py-0.5 bg-[#EFEFF4] text-[#26262E] border-[#D5D5DC]"
      data-testid="katlas-receipt-signed-chip"
    >
      <FileCheck2 className="h-3 w-3" /> Receipt: signed
    </span>
  );
}

/**
 * KATLAS governance record — rendered on Match + Offer receipts.
 * If a live attestation exists on the receipt, surfaces node id + attestation id/hash.
 */
export function KatlasRecord({ hash, receiptId, policyGate = "passed", actor, action, shared = [], withheld = [], attestation = null, timestamp = null, shareToken = null, car = null }) {
  const passed = policyGate === "passed";
  const mode = attestationMode(attestation);
  const isLive = mode === "live";
  return (
    <div
      className="rounded-md border border-border bg-[#FAFAFB] p-4 space-y-3"
      data-testid="katlas-governance-record"
      data-attestation-mode={mode || "none"}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-[#F25E1D]" /> Proof and timestamps · KATLAS governance record
        </div>
        <div className="flex flex-wrap gap-1.5">
          <KatlasModeChip overrideMode={mode || undefined} />
          <PolicyGatePassedChip passed={passed} />
          <ReceiptSignedChip />
        </div>
      </div>

      <div className="text-[12px] text-muted-foreground leading-relaxed">
        Every governed action leaves a receipt: what happened, who approved it, when, what was shared, what was withheld, and what proof was created.
      </div>

      {/* CAR block — Custody / Authority / Receipt */}
      {car && (
        <div
          className="rounded-md border border-[#F25E1D]/30 bg-white p-3 grid sm:grid-cols-3 gap-3 text-[12px]"
          data-testid="car-explainer-block"
        >
          <div>
            <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
              <Wallet className="h-3 w-3" /> Custody
            </div>
            <div className="mt-1 leading-relaxed">{car.custody}</div>
          </div>
          <div>
            <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
              <UserRound className="h-3 w-3" /> Authority
            </div>
            <div className="mt-1 leading-relaxed">{car.authority}</div>
          </div>
          <div>
            <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
              <FileCheck2 className="h-3 w-3" /> Receipt
            </div>
            <div className="mt-1 leading-relaxed">{car.receipt}</div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 text-[12px]" data-testid="proof-grid">
        {action && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Action recorded</div>
            <div className="mt-0.5">{action}</div>
          </div>
        )}
        {receiptId && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Local receipt ID</div>
            <div className="font-mono-ui mt-0.5 break-all">{receiptId}</div>
          </div>
        )}
        {timestamp && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Local timestamp</div>
            <div className="font-mono-ui mt-0.5">{timestamp}</div>
          </div>
        )}
        {actor && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Approved by · role</div>
            <div className="mt-0.5">{actor}</div>
          </div>
        )}
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Policy gate result</div>
          <div className="mt-0.5">{passed ? "passed" : "blocked"}</div>
        </div>
        {hash && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Local receipt hash</div>
            <div className="font-mono-ui mt-0.5 break-all">{hash}</div>
          </div>
        )}
        {shareToken && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Share token · public receipt</div>
            <div className="font-mono-ui mt-0.5 break-all">{shareToken}</div>
          </div>
        )}
        {shared.length > 0 && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Shared evidence / fields</div>
            <div className="text-[11px] mt-0.5">{shared.join(" · ")}</div>
          </div>
        )}
        {withheld.length > 0 && (
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Withheld evidence / fields</div>
            <div className="text-[11px] mt-0.5">{withheld.join(" · ")}</div>
          </div>
        )}
      </div>

      {/* KATLAS node attestation block — always rendered so the live/simulation
          status line is unambiguous to every reviewer. */}
      <div
        className="rounded-md border border-dashed border-[#F25E1D]/40 bg-white p-3 space-y-2"
        data-testid="katlas-node-attestation"
      >
        <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1.5">
          <Network className="h-3 w-3" /> KATLAS node attestation
        </div>
        {isLive && attestation ? (
          <>
            <div className="text-[12px] font-medium text-[#1F4A2D]" data-testid="node-attestation-status">
              Live node attestation: verified
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-[12px]">
              {attestation.node_id && (
                <div>
                  <div className="tracking-eyebrow text-muted-foreground">KATLAS mode · node</div>
                  <div className="font-mono-ui mt-0.5">LIVE · {attestation.node_id}</div>
                </div>
              )}
              {attestation.attestation_id && (
                <div>
                  <div className="tracking-eyebrow text-muted-foreground">Node attestation ID</div>
                  <div className="font-mono-ui mt-0.5 break-all">{attestation.attestation_id}</div>
                </div>
              )}
              {attestation.attestation_hash && (
                <div>
                  <div className="tracking-eyebrow text-muted-foreground">Node attestation hash / signature ref</div>
                  <div className="font-mono-ui mt-0.5 break-all">{attestation.attestation_hash}</div>
                </div>
              )}
              {attestation.timestamp && (
                <div>
                  <div className="tracking-eyebrow text-muted-foreground">Node timestamp</div>
                  <div className="font-mono-ui mt-0.5">{attestation.timestamp}</div>
                </div>
              )}
              <div>
                <div className="tracking-eyebrow text-muted-foreground">Verification status</div>
                <div className="mt-0.5">{attestation.verification_status || "verified"}</div>
              </div>
            </div>
          </>
        ) : attestation && attestation.mode === "fallback" ? (
          <>
            <div className="text-[12px] font-medium text-[#7A4A00]" data-testid="node-attestation-status">
              Live node attestation: not verified — simulation fallback
            </div>
            <div className="text-[11px] text-muted-foreground">
              KATLAS mode: <code>fallback_simulated</code> · Reason: {attestation.fallback_reason || "node unreachable or returned an error"}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-[12px]">
              {attestation.attestation_hash && (
                <div>
                  <div className="tracking-eyebrow text-muted-foreground">Simulated attestation hash</div>
                  <div className="font-mono-ui mt-0.5 break-all">{attestation.attestation_hash}</div>
                </div>
              )}
              {attestation.timestamp && (
                <div>
                  <div className="tracking-eyebrow text-muted-foreground">Timestamp</div>
                  <div className="font-mono-ui mt-0.5">{attestation.timestamp}</div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="text-[12px] font-medium text-[#7A2E0C]" data-testid="node-attestation-status">
              Live node attestation: not connected in this POC
            </div>
            <div className="text-[11px] text-muted-foreground">
              KATLAS mode: <code>simulation</code> · No live KATLAS node is connected. The proof above runs in-process and demonstrates the operating model that a live node would attest.
            </div>
          </>
        )}
        <div className="text-[11px] italic text-muted-foreground">
          KATLAS attests the governed action using metadata, roles, permissions and evidence references. Raw personal data and withheld assets are not sent to the node.
        </div>
      </div>

      <div className="text-[11px] italic text-muted-foreground border-t border-border pt-2 leading-relaxed">
        KATLAS records the governed action, not a copy of everyone's private data.
      </div>
    </div>
  );
}

/**
 * "How KATLAS works here" — drawer explaining the layer + live status.
 */
export function HowKatlasWorksButton({ variant = "outline", size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const status = useKatlasStatus();
  const mode = chipMode(status);
  const isLive = mode === "live";
  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        data-testid="how-katlas-works-btn"
        className={className}
      >
        <Info className="h-3.5 w-3.5 mr-1" /> How KATLAS works here
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="how-katlas-works-dialog">
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#F25E1D]" /> How KATLAS works here
          </DialogTitle>
          <DialogDescription className="sr-only">
            Plain-English explanation of the KATLAS attestation layer in the ClubCierge POC.
          </DialogDescription>
          <div className="flex gap-1.5 flex-wrap pt-1">
            <KatlasModeChip size="lg" />
          </div>
          <div className="space-y-3 text-sm leading-relaxed">
            <p>
              <b>KATLAS is the governance layer.</b> It does not generate the
              alignment narrative and it does not approve introductions.
              It attests the permissioned action.
            </p>
            <ul className="space-y-2">
              <li className="flex gap-2"><Sparkles className="h-3.5 w-3.5 mt-1 text-[#F25E1D] shrink-0" /><span><b>AI suggests</b> alignment between a member and a partner.</span></li>
              <li className="flex gap-2"><Wallet className="h-3.5 w-3.5 mt-1 text-[#4A5D4E] shrink-0" /><span><b>Wallets prove</b> the minimum fact needed — role, age band, approved introduction.</span></li>
              <li className="flex gap-2"><UserRound className="h-3.5 w-3.5 mt-1 text-[#4A5D4E] shrink-0" /><span><b>Parents &amp; officers approve</b> where required by policy.</span></li>
              <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 mt-1 text-[#F25E1D] shrink-0" /><span><b>KATLAS attests</b> the action — receiving metadata and evidence references, returning an attestation id + hash.</span></li>
              <li className="flex gap-2"><Lock className="h-3.5 w-3.5 mt-1 text-muted-foreground shrink-0" /><span>Withheld data stays withheld. The public share view redacts it.</span></li>
            </ul>
            <div className="rounded-md border border-dashed border-[#F25E1D]/40 bg-[#FFF7F2] p-3 text-[12px] space-y-1" data-testid="how-katlas-works-mode-block">
              {isLive ? (
                <>
                  <b>This deployment is in live node mode.</b> When an authorised action is approved, ClubCierge sends a minimal governance payload to a KATLAS node and the node returns an attestation. The node does not receive raw private data or withheld assets.
                  {status?.node_id && <div className="mt-1">Node: <code>{status.node_id}</code></div>}
                </>
              ) : mode === "fallback" ? (
                <>
                  <b>Live node was requested but unreachable.</b> ClubCierge has fallen back to a governance simulation for this approval so the workflow remains visible. The receipt shows fallback honestly.
                </>
              ) : (
                <>
                  <b>This deployment is running in KATLAS governance simulation mode.</b> It shows the operating model. No live KATLAS node attestation is created — the receipt records the shape of what an attestation would carry.
                </>
              )}
            </div>
            <p className="text-[12px] italic text-muted-foreground">
              KATLAS records the governed action, not a copy of everyone's private data.
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              <X className="h-3.5 w-3.5 mr-1" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
